import { Check, Close } from '@mui/icons-material';
import { Button, DialogActions, DialogContent, DialogTitle, Switch, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material/styles';
import React, { FunctionComponent, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import asDialog from '@/hocs/as-dialog';
import { useTranslation } from '@/hooks';

const useStyles = makeStyles()((theme: Theme) => ({
  targetsButton: {
    height: '20px',
    width: '20px',
    fontSize: '1.1em',
  },

  iconStyle: {
    color: theme.palette.primary.dark,
    marginRight: 8,
    marginBottom: 2,
  },

  error: {
    height: '10px',
    color: '#cc0000',
  },
  inputLabel: {
    '&.shrink': {
      transform: 'translate(0px, 6px) scale(0.75)',
    },
  },
  flexContainer: {
    display: 'flex',
  },
  label: {
    marginLeft: 8,
  },
  cancelButton: {
    color: 'gray',
  },
}));

export type OEESettings = {
  includeScrap: boolean | null;
  includeSpeedLoss: boolean | null;
  targets?: {
    oee?: number | null | undefined;
    oee2?: number | null | undefined;
    oee3?: number | null | undefined;
    tcu?: number | null | undefined;
  };
};

type Properties = {
  settings: OEESettings;
  onChange: (settings: OEESettings) => void;
  onClose: () => void;
};

const SettingsDialogContent: FunctionComponent<Properties> = ({ settings, onChange, onClose }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [oee, setOee] = useState(settings.targets?.oee);
  const [oee2, setOee2] = useState(settings.targets?.oee2);
  const [oee3, setOee3] = useState(settings.targets?.oee3);
  const [tcu, setTcu] = useState(settings.targets?.tcu);

  const [includeScrap, setIncludeScrap] = useState<null | boolean>(settings.includeScrap);
  const [includeSpeedLoss, setIncludeSpeedLoss] = useState<null | boolean>(settings.includeSpeedLoss);

  const oeeTranslation = t(['shared:oee'], { defaultValue: 'OEE' });
  const oee1Label = oeeTranslation + '1: ';
  const oee2Label = oeeTranslation + '2: ';
  const oee3Label = oeeTranslation + '3: ';
  const tcuLabel = t(['shared:tcu'], { defaultValue: 'OEE' });

  const onSubmit = () => {
    onChange({ targets: { oee, oee2, oee3, tcu }, includeScrap, includeSpeedLoss });
    onClose();
  };

  return (
    <>
      <DialogTitle>
        <Typography id="transition-modal-title" variant="h6">
          {t('shared:oeeSettings', 'OEE settings')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <div className={classes.flexContainer}>
          <div>
            <div className={classes.label}>{t(['shared:includeScrap'])}</div>
            <Switch
              value="include-scrap"
              disabled={includeScrap === null}
              checked={includeScrap === true}
              onChange={(_, v) => setIncludeScrap(v)}
              color="primary"
            />
          </div>
          <div style={{ marginLeft: 16 }}>
            <div className={classes.label}>{t(['shared:includeSpeedLoss'], { defaultValue: 'Include speedloss' })}</div>
            <Switch
              value="include-speedloss"
              disabled={includeSpeedLoss === null}
              checked={includeSpeedLoss === true}
              onChange={(_, v) => setIncludeSpeedLoss(v)}
              color="primary"
            />
          </div>
        </div>
        {!settings.targets ? null : (
          <>
            <TextField
              label={oee1Label}
              onChange={(e) => setOee(Number(e.target.value))}
              defaultValue={(oee ?? ('N/A' as unknown)) as number}
              type="number"
              margin="normal"
              variant="filled"
              fullWidth
              InputLabelProps={{
                classes: {
                  root: classes.inputLabel,
                  shrink: 'shrink',
                },
              }}
            />
            <TextField
              label={oee2Label}
              onChange={(e) => setOee2(Number(e.target.value))}
              defaultValue={(oee2 ?? ('N/A' as unknown)) as number}
              type="number"
              margin="normal"
              variant="filled"
              fullWidth
              InputLabelProps={{
                classes: {
                  root: classes.inputLabel,
                  shrink: 'shrink',
                },
              }}
            />
            <TextField
              label={oee3Label}
              onChange={(e) => setOee3(Number(e.target.value))}
              defaultValue={(oee3 ?? ('N/A' as unknown)) as number}
              type="number"
              margin="normal"
              variant="filled"
              fullWidth
              InputLabelProps={{
                classes: {
                  root: classes.inputLabel,
                  shrink: 'shrink',
                },
              }}
            />
            <TextField
              label={tcuLabel}
              onChange={(e) => setTcu(Number(e.target.value))}
              defaultValue={(tcu ?? ('N/A' as unknown)) as number}
              type="number"
              margin="normal"
              variant="filled"
              fullWidth
              InputLabelProps={{
                classes: {
                  root: classes.inputLabel,
                  shrink: 'shrink',
                },
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" className={classes.cancelButton} onClick={() => onClose()}>
          <Close className={classes.iconStyle} />
          {t(['shared:cancel'])}
        </Button>
        <Button variant="text" onClick={() => onSubmit()}>
          <Check className={classes.iconStyle} />
          {t(['shared:save'])}
        </Button>
      </DialogActions>
    </>
  );
};

export default asDialog({ Component: SettingsDialogContent });
